import * as React from 'react';
import { useState } from 'react';
import { Button } from '@patternfly/react-core';
import { CreditCardIcon } from '@patternfly/react-icons';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { SetupIntentResponse } from '@app/Subscription/types/subscription';
import { AppModal } from '@app/components/AppModal';
import { LogoBadge } from '@app/components/LogoBadge';
import { GradientButton } from '@app/components/GradientButton';
import { colors, formResetStyle, radii } from '@app/components/designTokens';

let _stripePromise: Promise<Stripe | null> | null = null;
function getStripe(): Promise<Stripe | null> {
    if (!_stripePromise) {
        _stripePromise = loadStripe(process.env['STRIPE_PUB_KEY'] + '');
    }
    return _stripePromise;
}

interface AddPaymentMethodModalProps {
    isOpen: boolean;
    setupIntent: SetupIntentResponse | null;
    onClose: () => void;
    onSuccess: (paymentMethodId: string | null) => void;
}

export const AddPaymentMethodModal: React.FC<AddPaymentMethodModalProps> = ({
    isOpen,
    setupIntent,
    onClose,
    onSuccess
}) => {
    if (!setupIntent) return null;
    return (
        <AppModal isOpen={isOpen} onClose={onClose} width="md">
            <div style={{ padding: '40px 32px 32px' }}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <LogoBadge size={48}>
                        <CreditCardIcon style={{ fontSize: 24, color: colors.boltGold }} />
                    </LogoBadge>
                    <h1 style={{ color: colors.title, fontSize: '24px', fontWeight: 700, margin: 0, marginBottom: '6px', letterSpacing: '-0.5px' }}>
                        Add a card
                    </h1>
                    <p style={{ color: colors.subtle, fontSize: '14px', margin: 0 }}>
                        Card details are processed securely by Stripe
                    </p>
                </div>
                <Elements stripe={getStripe()} options={{ clientSecret: setupIntent.clientSecret }}>
                    <AddCardForm onSuccess={onSuccess} onCancel={onClose} />
                </Elements>
            </div>
        </AppModal>
    );
};

interface AddCardFormProps {
    onSuccess: (paymentMethodId: string | null) => void;
    onCancel: () => void;
}

const AddCardForm: React.FC<AddCardFormProps> = ({ onSuccess, onCancel }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setSubmitting(true);
        setErrorMessage(null);

        const returnUrl = window.location.origin + '/plan';
        const result = await stripe.confirmSetup({
            elements,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required'
        });

        if (result.error) {
            setErrorMessage(result.error.message || 'Failed to save card');
            setSubmitting(false);
            return;
        }
        const pm = result.setupIntent?.payment_method;
        const paymentMethodId = typeof pm === 'string' ? pm : pm?.id ?? null;
        onSuccess(paymentMethodId);
    };

    return (
        <form onSubmit={handleSubmit} style={formResetStyle}>
            <PaymentElement />
            {errorMessage && (
                <div style={{
                    marginTop: '12px',
                    color: colors.danger,
                    fontSize: '14px',
                    padding: '12px 14px',
                    background: '#fef2f2',
                    border: `1px solid #fecaca`,
                    borderRadius: radii.input
                }}>
                    {errorMessage}
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
                <Button variant="link" onClick={onCancel} isDisabled={submitting} style={{ color: colors.subtle, fontWeight: 600 }}>
                    Cancel
                </Button>
                <GradientButton
                    type="submit"
                    isDisabled={!stripe || !elements || submitting}
                    isLoading={submitting}
                >
                    {submitting ? 'Saving…' : 'Save card'}
                </GradientButton>
            </div>
        </form>
    );
};
